import type { ReactElement, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';

export interface InventoryUserListColumn {
  label: ReactNode;
  className?: string;
}

interface InventoryUserListDialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  columns: InventoryUserListColumn[];
  isLoading: boolean;
  error: unknown;
  isEmpty: boolean;
  emptyMessage: string;
  /** Loading-state text. Defaults to the user-list wording. */
  loadingMessage?: string;
  /** Error-state text. Defaults to the user-list wording. */
  errorMessage?: string;
  /** When provided, an error state shows a Retry button wired to this. */
  onRetry?: () => void;
  /** Relabels + disables the Retry button while a refetch is in flight. */
  isRetrying?: boolean;
  contentClassName?: string;
  titleClassName?: string;
  children: ReactNode;
}

// Standard sizing for every inventory list modal: auto-fit to content, capped
// at the viewport (width up to 90vw, height up to 80vh), and anything larger
// scrolls *inside* the dialog (overflow-auto on the inner box) rather than
// pushing it off-screen. Keep this here so consumers never hard-code a width.
const DEFAULT_CONTENT_CLASS =
  'sm:w-fit sm:max-w-[90vw] max-h-[80vh] overflow-hidden flex flex-col';
const DEFAULT_TITLE_CLASS =
  'leading-relaxed line-clamp-3 break-words overflow-hidden';

/**
 * Shared shell for inventory list modals — "users / projects that have
 * <inventory item>" (Extensions / MCP / Instructions / Plugins / Projects /
 * Hooks). Each per-item modal owns its data hook and row rendering; this
 * component provides the Dialog + table chrome, the standard auto-sizing, and
 * the loading / error / empty states so they stay visually consistent. Loading
 * and error wording default to the user-list case and can be overridden.
 */
export function InventoryUserListDialog({
  open,
  onClose,
  title,
  description,
  columns,
  isLoading,
  error,
  isEmpty,
  emptyMessage,
  loadingMessage = 'Loading users...',
  errorMessage = 'Failed to load users',
  onRetry,
  isRetrying = false,
  contentClassName = DEFAULT_CONTENT_CLASS,
  titleClassName = DEFAULT_TITLE_CLASS,
  children,
}: InventoryUserListDialogProps): ReactElement {
  const colSpan = columns.length;
  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className={contentClassName}>
        <DialogHeader>
          <DialogTitle className={titleClassName}>{title}</DialogTitle>
          {description !== undefined && (
            <DialogDescription>{description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="rounded-md border mt-4 overflow-hidden flex-1 min-h-0">
          {/* The vertical scroller. It is bounded to an explicit height (not
              just `h-full`) so the scrollbar always engages even if the
              `flex-1 / min-h-0` chain against DialogContent's grid base fails to
              resolve — which is what let a single tall row (a user with ~40
              Projects) push the dialog past the viewport and hide the rest of
              the list (KIR-3991). The cap is `80vh − 14rem`, derived from the
              dialog's own `max-h-[80vh]`: the ~14rem reserve covers the chrome
              (p-6 padding + up to a 3-line title + description + gaps), so
              `chrome + scroller ≤ 80vh` always holds and the outer
              `overflow-hidden` can never clip the bottom rows — and if the
              80vh cap itself fails to apply, `chrome + ≤66vh` still fits inside
              the viewport. `overflow-y-auto` leaves horizontal scrolling to the
              inner Table's own `overflow-x-auto` wrapper. */}
          <div className="overflow-y-auto h-full max-h-[calc(80vh_-_14rem)]">
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((col, idx) => (
                    <TableHead key={idx} className={col.className}>
                      {col.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={colSpan}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {loadingMessage}
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} className="py-8">
                      <div className="flex flex-col items-center gap-2">
                        <p className="text-sm text-destructive">
                          {errorMessage}
                        </p>
                        {onRetry && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onRetry()}
                            disabled={isRetrying}
                          >
                            {isRetrying ? 'Retrying…' : 'Retry'}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : isEmpty ? (
                  <TableRow>
                    <TableCell
                      colSpan={colSpan}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                ) : (
                  children
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
